import { getWorkspaceIdFromCurrentUrl } from '@/context/current-workspace';
import { toggle } from '@/features/sidebar';
import { IconButton } from '@/layouts/authenticated/components/top-navigation/components/icon-button';
import { servicesApi, servicesQuery } from '@/services/api';
import { servicesApi as untypedServicesApi } from '@/services/api/deprecated';
import {
  setCopilotConfig,
  type APICopilotGetUploadSignature,
  type APICopilotGetFileDownloadUrl,
  type CopilotUploadSignatureResponse,
} from '@customerio/hydra-copilot';
import { CopilotProvider, useCopilotSession, type MarkdownComponents } from '@customerio/hydra-copilot/react';
import { themeVars } from '@customerio/pluma-components/css/theme-tokens';
import {
  Box,
  Code,
  Divider,
  Emphasis,
  Heading,
  Image,
  Link,
  List,
  ListItem,
  Paragraph,
  Strong,
  Table,
  TableTbody,
  TableTd,
  TableTh,
  TableThead,
  TableTr,
  Text,
} from '@customerio/pluma-components/react';
import { Chat } from './chat';
import { History } from './history';
import { authStore } from '@/services/auth/store';

setCopilotConfig({
  async startSession() {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.createCopilotSession({
      params: { accountId },
      body: {},
    });

    return {
      id: response.session_id,
      tools: response.tools,
    };
  },
  getUploadSignature: (async (
    payload: Parameters<APICopilotGetUploadSignature>[0],
  ): Promise<CopilotUploadSignatureResponse> => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesQuery.getCopilotFileUploadSignature.mutate({
      params: {
        accountId,
        sessionId: payload.id,
      },
      body: {
        size: payload.size,
        mime_type: payload.mime_type,
      },
    });

    return response.body as CopilotUploadSignatureResponse;
  }) satisfies APICopilotGetUploadSignature,
  getFileDownloadUrl: (async (payload: Parameters<APICopilotGetFileDownloadUrl>[0]) => {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.getCopilotFileDownloadUrl({
      params: {
        accountId,
        sessionId: payload.id,
        filename: payload.filename,
      },
    });

    return {
      url: response.url,
    };
  }) satisfies APICopilotGetFileDownloadUrl,
  async getSession(id) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const response = await servicesApi.getCopilotSession({
      params: { accountId, sessionId: Number(id) },
    });

    return {
      meta: { name: response.name },
      messages: response.messages,
      tools: response.tools,
    };
  },
  async sendStreamingMessage({ onMessage, id, ...payload }) {
    let currentLine = -1;
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    await untypedServicesApi.post(
      `/accounts/${accountId}/copilot/${id}`,
      {
        environment_id: getWorkspaceIdFromCurrentUrl(),
        stream: true,
        ...payload,
      },
      {
        onDownloadProgress: (progressEvent) => {
          const response = progressEvent.event.currentTarget.response;
          const lines = response.split('\n');

          for (let index = 0; index < lines.length; index++) {
            const line = lines[index].trim();
            if (line.length === 0) {
              continue;
            }

            // skip lines that have already been processed
            if (index <= currentLine) {
              continue;
            }

            // we are now processing this line
            currentLine = index;

            try {
              onMessage(JSON.parse(line));
            } catch (error) {
              // eslint-disable-next-line no-console
              console.error('Error parsing message from server:', line, error);
              // TODO: If we can't parse the message, we should kill this exchange entirely
              // This can happen if the server sends an incomplete message
            }
          }
        },
      },
    );
  },
  async endSession() {
    // noop
  },
  async generate(payload) {
    const auth = authStore.get();
    const accountId = auth?.account?.id;

    if (accountId == null) {
      throw new Error('No account ID found in auth store');
    }

    const { data } = await untypedServicesApi.post(`/accounts/${accountId}/copilot_generate`, {
      ...payload,
      environment_id: getWorkspaceIdFromCurrentUrl(),
    });

    return data;
  },
});

const components: MarkdownComponents = {
  p: ({ children }) => (
    <Paragraph style={{ lineHeight: 1.65 }} color="inherit">
      {children}
    </Paragraph>
  ),
  h1: ({ children }) => (
    <Heading level="2" color="inherit">
      {children}
    </Heading>
  ),
  h2: ({ children }) => (
    <Heading level="3" color="inherit">
      {children}
    </Heading>
  ),
  h3: ({ children }) => (
    <Heading level="4" color="inherit">
      {children}
    </Heading>
  ),
  h4: ({ children }) => (
    <Heading level="5" color="inherit">
      {children}
    </Heading>
  ),
  h5: ({ children }) => (
    <Heading level="6" color="inherit">
      {children}
    </Heading>
  ),
  h6: ({ children }) => (
    <Heading level="6" color="inherit">
      {children}
    </Heading>
  ),
  strong: ({ children }) => <Strong>{children}</Strong>,
  em: ({ children }) => <Emphasis>{children}</Emphasis>,
  pre: ({ children }) => <>{children}</>,
  code: ({ className, children }) => {
    const language = typeof className === 'string' ? className.replace('language-', '') : '';
    const isMultiline = typeof children === 'string' && children.includes('\n');
    const isBlock = language.length > 0 || isMultiline;

    return isBlock ? (
      <Box p="150" backgroundColor="subtle" style={{ borderRadius: themeVars.radius['6'] }} overflowX="auto">
        <Box display="flex" alignItems="center" justifyContent="space-between" mb="100">
          <Text size="xs" color="subtle" weight="medium">
            {language ?? 'plaintext'}
          </Text>
        </Box>
        <Text as="pre" style={{ margin: 0, maxWidth: '100%', overflowX: 'auto' }}>
          <Text
            as="code"
            style={{
              whiteSpace: 'pre',
              background: 'transparent',
              fontFamily: themeVars['font-family']['mono'],
              fontWeight: 'normal',
            }}
          >
            {children}
          </Text>
        </Text>
      </Box>
    ) : (
      <Code>{children}</Code>
    );
  },
  blockquote: ({ children }) => (
    <Box
      pl="200"
      as="blockquote"
      style={{
        borderLeft: `${themeVars.border['width-050']} solid ${themeVars.color['border-minimal']}`,
        marginLeft: 0,
        marginRight: 0,
      }}
    >
      {children}
    </Box>
  ),
  ul: ({ children }) => <List type="unordered">{children}</List>,
  ol: ({ children }) => <List type="ordered">{children}</List>,
  li: ({ children }) => <ListItem>{children}</ListItem>,
  a: ({ href, children }) => (
    <Link href={href} isExternal={true} variant="secondary">
      {children}
    </Link>
  ),
  img: ({ src, alt }) => <Image src={src} alt={alt} style={{ maxWidth: '100%', height: 'auto' }} />,
  hr: () => <Divider my="100" />,
  table: ({ children }) => (
    <Box overflowX="auto">
      <Table caption="Table">{children}</Table>
    </Box>
  ),
  thead: ({ children }) => <TableThead>{children}</TableThead>,
  tbody: ({ children }) => <TableTbody>{children}</TableTbody>,
  tr: ({ children }) => <TableTr>{children}</TableTr>,
  th: ({ children }) => <TableTh>{children}</TableTh>,
  td: ({ children }) => <TableTd>{children}</TableTd>,
};

export function Copilot() {
  const session = useCopilotSession({ isPrimary: true });

  return (
    <>
      <IconButton
        label="AI Assistant"
        tooltip="Assistant"
        icon="chat"
        onClick={() => {
          toggle('copilot');
        }}
      />
      <CopilotProvider components={components}>
        <History session={session} />
        <Chat session={session} />
      </CopilotProvider>
    </>
  );
}
